import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Lightbulb, Handshake, Leaf } from 'lucide-react';
import SEO from '../components/SEO';
import useScrollAnimation from '../hooks/useScrollAnimation';
import './QuienesSomos.css';

const QuienesSomos = () => {
  const [storyRef, storyVisible] = useScrollAnimation();
  const [teamRef, teamVisible] = useScrollAnimation();
  const [valuesRef, valuesVisible] = useScrollAnimation();

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Quiénes Somos - Artelaser3D",
    "description": "Conoce al equipo de Artelaser3D, expertos en grabado láser e impresión 3D en Asturias",
    "mainEntity": {
      "@type": "Organization",
      "name": "Artelaser3D",
      "founders": [
        {
          "@type": "Person",
          "name": "Especialista en Grabado Láser",
          "jobTitle": "Co-fundador - Especialista en Grabado Láser"
        },
        {
          "@type": "Person",
          "name": "Especialista en Impresión 3D",
          "jobTitle": "Co-fundador - Especialista en Impresión 3D"
        }
      ],
      "description": "Artelaser3D es una empresa asturiana especializada en grabado láser e impresión 3D, fundada por dos socios apasionados por la tecnología y el diseño.",
      "areaServed": "Asturias, España"
    }
  };

  const values = [
    {
      icon: <Target size={28} />,
      title: 'Precisión',
      description: 'Cada proyecto merece atención al detalle milimétrica. No hay espacio para errores en nuestro trabajo.'
    },
    {
      icon: <Lightbulb size={28} />,
      title: 'Innovación',
      description: 'Estamos constantemente explorando nuevas técnicas, materiales y tecnologías para ofrecerte lo mejor.'
    },
    {
      icon: <Handshake size={28} />,
      title: 'Compromiso',
      description: 'Tu satisfacción es nuestra prioridad. Nos involucramos en cada proyecto como si fuera nuestro.'
    },
    {
      icon: <Leaf size={28} />,
      title: 'Sostenibilidad',
      description: 'Optimizamos materiales y procesos para minimizar desperdicios y reducir nuestra huella ambiental.'
    }
  ];

  return (
    <>
      <SEO 
        title="Quiénes Somos"
        description="Conoce a los fundadores de Artelaser3D. Dos especialistas apasionados por el grabado láser y la impresión 3D en Asturias. Más de 500 proyectos completados."
        keywords="quiénes somos Artelaser3D, fundadores empresa láser Asturias, equipo impresión 3D Asturias, empresa grabado láser Oviedo"
        canonicalUrl="https://artelaser3d.es/quienes-somos"
        schema={schema}
      />

      <section className="page-hero page-hero--about">
        <div className="page-hero__container">
          <span className="page-hero__badge">Quiénes Somos</span>
          <h1 className="page-hero__title">
            La unión de dos
            <span className="text-primary"> pasiones</span>
          </h1>
          <p className="page-hero__description">
            Artelaser3D nace de la fusión entre la precisión del grabado láser 
            y la creatividad de la impresión 3D. Dos especialistas, una visión.
          </p>
        </div>
      </section>

      <section 
        ref={storyRef}
        className={`section section--story ${storyVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <div className="story-grid">
            <div className="story-content">
              <span className="section__badge">Nuestra Historia</span>
              
              <h2>De la pasión al proyecto</h2>
              
              <p>
                Todo comenzó con dos amigos y una visión compartida: combinar tecnología 
                y artesanía para crear piezas únicas. Uno especializado en <strong>grabado 
                láser</strong> con años de experiencia en personalización de productos, 
                el otro experto en <strong>impresión 3D</strong> con formación en 
                diseño industrial.
              </p>
              
              <p>
                Juntos fundaron Artelaser3D en Asturias, con el objetivo de ofrecer 
                servicios de alta calidad que combinen precisión técnica con diseño 
                creativo. Desde entonces, hemos trabajado con cientos de clientes 
                satisfechos, desde particulares que buscan regalos únicos hasta 
                empresas que necesitan prototipos y producción en serie.
              </p>

              <div className="story-stats">
                <div className="story-stat">
                  <span className="story-stat__number">500+</span>
                  <span className="story-stat__label">Proyectos completados</span>
                </div>
                <div className="story-stat">
                  <span className="story-stat__number">2</span>
                  <span className="story-stat__label">Especialistas apasionados</span>
                </div>
                <div className="story-stat">
                  <span className="story-stat__number">98%</span>
                  <span className="story-stat__label">Clientes satisfechos</span>
                </div>
              </div>
            </div>

            <div className="story-visual">
              <div className="story-image">
                <img
                  src="/images/oficina.png"
                  alt="Oficina Artelaser3D"
                  className="story-image__img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section 
        ref={teamRef}
        className={`section section--team ${teamVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">El Equipo</span>
            <h2>Conoce a los fundadores</h2>
          </header>

          <div className="team-grid">
            <article className="team-member">
              <div className="team-member__photo">
                <img
                  src="/images/pexels-optlasers-30983996.jpg"
                  alt="Especialista en Grabado Láser - Trabajando con equipo láser"
                  className="team-member__img"
                />
              </div>
              <div className="team-member__info">
                <span className="team-member__role">Co-fundador</span>
                <h3>Especialista en Grabado Láser</h3>
                <p>
                  Con años de experiencia en grabado y corte láser, domina la 
                  personalización de madera, acrílico, cuero y metal. Su ojo 
                  para el detalle garantiza resultados impecables en cada proyecto.
                </p>
                <div className="team-member__specialties">
                  <span className="team-member__tag">Grabado Láser</span>
                  <span className="team-member__tag">Corte Láser</span>
                  <span className="team-member__tag">Diseño Vectorial</span>
                </div>
              </div>
            </article>

            <article className="team-member">
              <div className="team-member__photo">
                <img
                  src="/images/pexels-jakubzerdzicki-20688553.jpg"
                  alt="Especialista en Impresión 3D - Trabajando con impresora 3D"
                  className="team-member__img"
                />
              </div>
              <div className="team-member__info">
                <span className="team-member__role">Co-fundador</span>
                <h3>Especialista en Impresión 3D</h3>
                <p>
                  Formado en diseño industrial y prototipado, domina tecnologías 
                  FDM y resina. Su pasión por la innovación material transforma 
                  ideas en objetos funcionales con precisión milimétrica.
                </p>
                <div className="team-member__specialties">
                  <span className="team-member__tag">Impresión 3D</span>
                  <span className="team-member__tag">Modelado 3D</span>
                  <span className="team-member__tag">Prototipado</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section 
        ref={valuesRef}
        className={`section section--values ${valuesVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Nuestros Valores</span>
            <h2>Lo que nos define</h2>
          </header>

          <div className="values-grid">
            {values.map((value, index) => (
              <article key={index} className="value-card">
                <div className="value-card__icon-wrapper">
                  <div className="value-card__icon">{value.icon}</div>
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="about-cta-wrapper">
        <div className="about-cta">
          <h2>¿Quieres conocernos mejor?</h2>
          <p>
            Estamos en Asturias pero trabajamos con clientes de toda España.
            No dudes en contactarnos para cualquier consulta.
          </p>
          <div className="about-cta__buttons">
            <Link to="/servicios" className="about-cta__btn about-cta__btn--outline">
              Ver Servicios
            </Link>
            <Link to="/contacto" className="about-cta__btn about-cta__btn--primary">
              Contactar
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuienesSomos;
